import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight, Calendar, Users, Clock, Sparkles } from "lucide-react";
import { SunriseCircle } from "../components/icons/SunriseCircle";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PACKAGES, INCLUDED, FAQS, IMAGES, GROUP } from "../data/content";
import { FadeUp, Stagger, StaggerItem } from "../components/Reveal";
import { Marquee } from "../components/Marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Packages() {
  const [loadingId, setLoadingId] = useState(null);
  const [founding, setFounding] = useState({ active: true, left: null, total: null });
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({ name: "", email: "", phone: "" });
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/founding-status`).then((r) => setFounding(r.data)).catch(() => {});
  }, []);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistForm.name.trim() || !waitlistForm.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setWaitlistSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        name: waitlistForm.name,
        email: waitlistForm.email,
        phone: waitlistForm.phone,
        cohort: `${GROUP.name} — next cohort`,
      });
      setWaitlistDone(true);
      toast.success("You're on the list. We'll be in touch about the next cohort.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const handleCheckout = async (pkgId) => {
    setLoadingId(pkgId);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      console.error(e);
      toast.error("We couldn't start checkout. Please try again or book a call.");
      setLoadingId(null);
    }
  };

  return (
    <div data-testid="packages-page" className="bg-brand-cream">
      <section className="pt-40 md:pt-52 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeUp><span className="overline">Investment Packages</span></FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-brand-forest tracking-tight leading-[0.95] mt-6 max-w-4xl">
              An investment in becoming.
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="text-brand-inkSoft text-lg mt-8 max-w-xl leading-relaxed">
              Every experience includes one-on-one sessions, a personalized transformation plan,
              accountability, mindset coaching, and access to The House of Becoming community.
            </p>
          </FadeUp>
          {founding.active && (
            <FadeUp delay={0.3}>
              <div className="mt-8 inline-flex items-start sm:items-center gap-3 bg-brand-forest text-brand-cream px-6 py-4 rounded-2xl max-w-2xl" data-testid="founding-banner">
                <SunriseCircle className="text-brand-goldBright shrink-0 mt-1 sm:mt-0" size={20} />
                <span className="text-sm leading-relaxed">
                  <strong className="text-brand-goldBright">Legacy Member Offer —</strong> just one 50% off spot for each experience, then it reverts to full price. Split into interest-free payments with Klarna or Afterpay at checkout.
                </span>
              </div>
            </FadeUp>
          )}
        </div>
      </section>

      {/* cards */}
      <section className="pb-24" data-testid="package-cards">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-8 items-stretch">
          {PACKAGES.map((pkg) => {
            const fActive = !!(founding[pkg.id] && founding[pkg.id].active);
            return (
            <FadeUp key={pkg.id} delay={pkg.featured ? 0.1 : 0}>
              <div
                data-testid={`package-card-${pkg.id}`}
                className={`relative h-full flex flex-col rounded-[2rem] p-10 transition-transform duration-500 hover:-translate-y-2 ${
                  pkg.featured
                    ? "bg-brand-forest text-brand-cream shadow-2xl md:scale-[1.03]"
                    : "bg-brand-beige/50 text-brand-forest"
                }`}
              >
                {pkg.featured && (
                  <span className="absolute top-8 right-8 overline text-brand-goldBright">Most Chosen</span>
                )}
                <SunriseCircle className={pkg.featured ? "text-brand-goldBright" : "text-brand-gold"} size={34} strokeWidth={1.3} />
                <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-8">{pkg.name}</h2>
                <p className={`mt-2 text-sm tracking-wide ${pkg.featured ? "text-brand-cream/70" : "text-brand-inkSoft"}`}>
                  {pkg.duration} · {pkg.cadence}
                </p>

                {fActive && (
                  <span className={`inline-block text-xs font-semibold tracking-wide px-3 py-1 rounded-full mt-6 ${pkg.featured ? "bg-brand-goldBright text-brand-forest" : "bg-brand-forest text-brand-cream"}`}>
                    Legacy Member · 50% Off · 1 spot
                  </span>
                )}
                <div className={`flex items-end gap-3 ${fActive ? "mt-4" : "mt-8"}`}>
                  <span className="font-serif text-6xl tracking-tight">${(fActive ? pkg.price : pkg.originalPrice).toLocaleString()}</span>
                  {fActive ? (
                    <span className={`mb-2 text-2xl line-through ${pkg.featured ? "text-brand-cream/40" : "text-brand-sage"}`}>${pkg.originalPrice.toLocaleString()}</span>
                  ) : (
                    <span className={`mb-2 text-sm ${pkg.featured ? "text-brand-cream/60" : "text-brand-inkSoft"}`}>paid in full</span>
                  )}
                </div>
                <p className={`text-sm italic mt-1 ${pkg.featured ? "text-brand-goldBright" : "text-brand-gold"}`}>{pkg.monthly}</p>

                <ul className="mt-8 space-y-4 flex-1">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check size={18} className={`shrink-0 mt-1 ${pkg.featured ? "text-brand-goldBright" : "text-brand-gold"}`} />
                      <span className={pkg.featured ? "text-brand-cream/85" : "text-brand-inkSoft"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`checkout-btn-${pkg.id}`}
                  onClick={() => handleCheckout(pkg.id)}
                  disabled={loadingId === pkg.id}
                  className={`group mt-10 inline-flex items-center justify-center gap-3 w-full px-8 py-4 rounded-full text-sm tracking-wide transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60 ${
                    pkg.featured
                      ? "bg-brand-cream text-brand-forest hover:shadow-2xl"
                      : "bg-brand-forest text-brand-cream hover:shadow-xl"
                  }`}
                >
                  {loadingId === pkg.id ? (
                    <><Loader2 size={18} className="animate-spin" /> Preparing checkout…</>
                  ) : (
                    <>Reserve your place <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" /></>
                  )}
                </button>
              </div>
            </FadeUp>
            );
          })}
        </div>

        <FadeUp>
          <p className="text-center text-brand-inkSoft mt-10 text-sm">
            Not ready to commit? <a href="/contact" data-testid="packages-book-call" className="text-brand-forest link-underline">Book a free discovery call →</a>
          </p>
        </FadeUp>
      </section>

      {/* group circle */}
      <section className="pb-8 md:pb-16" data-testid="group-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="relative overflow-hidden rounded-[2rem] bg-brand-forest text-brand-cream p-8 md:p-14 grain">
            <div className="relative z-10 grid md:grid-cols-2 gap-10 items-center">
              <div>
                <span className="overline text-brand-goldBright">The Gathering · Group Circle</span>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-5 leading-[1.05]">{GROUP.name}</h2>
                <p className="text-brand-cream/50 italic mt-2">{GROUP.subtitle}</p>
                <p className="text-brand-cream/80 leading-relaxed mt-6">{GROUP.blurb}</p>
                <div className="mt-8 space-y-3 text-sm text-brand-cream/85">
                  <div className="flex items-center gap-3"><Calendar size={16} className="text-brand-goldBright shrink-0" /> {GROUP.schedule}</div>
                  <div className="flex items-center gap-3"><Clock size={16} className="text-brand-goldBright shrink-0" /> {GROUP.starts}</div>
                  <div className="flex items-center gap-3"><Users size={16} className="text-brand-goldBright shrink-0" /> Intimate circle · up to {GROUP.capacity} women</div>
                </div>
              </div>

              <div className="md:justify-self-end w-full md:max-w-xs bg-brand-cream/5 border border-brand-forestSoft rounded-2xl p-8 text-center">
                <SunriseCircle className="text-brand-goldBright mx-auto" size={30} />
                <div className="mt-4 font-serif text-6xl tracking-tight" data-testid="group-price">${GROUP.price}</div>
                <p className="text-brand-cream/60 text-sm mt-1">4-week group experience</p>
                <ul className="mt-6 space-y-3 text-left">
                  {GROUP.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-brand-cream/80">
                      <Check size={16} className="text-brand-goldBright shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid="group-add-waitlist-btn"
                  onClick={() => { setWaitlistDone(false); setWaitlistOpen(true); }}
                  className="group mt-8 inline-flex items-center justify-center gap-3 w-full px-8 py-4 rounded-full text-sm tracking-wide bg-brand-cream text-brand-forest hover:-translate-y-0.5 hover:shadow-2xl transition-transform duration-300"
                >
                  Add to waitlist <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
                <p className="text-brand-cream/50 text-xs mt-4">Join the waitlist to be notified when the next cohort opens</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* waitlist dialog */}
      <Dialog open={waitlistOpen} onOpenChange={setWaitlistOpen}>
        <DialogContent className="bg-brand-cream border-brand-beige sm:max-w-md" data-testid="waitlist-dialog">
          {waitlistDone ? (
            <div className="text-center py-6" data-testid="waitlist-success">
              <SunriseCircle className="text-brand-gold mx-auto" size={40} />
              <h3 className="font-serif text-3xl text-brand-forest mt-5">You're on the list.</h3>
              <p className="text-brand-inkSoft mt-3 leading-relaxed">
                Thank you for your interest in {GROUP.name}. We'll reach out personally when the
                next cohort opens — dates are set based on enrollment.
              </p>
              <button
                data-testid="waitlist-close-btn"
                onClick={() => setWaitlistOpen(false)}
                className="mt-8 inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm tracking-wide bg-brand-forest text-brand-cream hover:-translate-y-0.5 transition-transform duration-300"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 text-brand-gold">
                  <Sparkles size={16} />
                  <span className="overline">Next Cohort Waitlist</span>
                </div>
                <DialogTitle className="font-serif text-3xl text-brand-forest tracking-tight text-left mt-1">
                  Join the waitlist
                </DialogTitle>
                <DialogDescription className="text-brand-inkSoft text-left leading-relaxed">
                  Add your details and we'll invite you first when the next cohort opens —
                  dates are set based on enrollment.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleWaitlist} className="space-y-4 mt-2" data-testid="waitlist-form">
                <div>
                  <Label htmlFor="wl-name" className="text-brand-forest text-sm">Full name</Label>
                  <Input
                    id="wl-name"
                    data-testid="waitlist-name-input"
                    value={waitlistForm.name}
                    onChange={(e) => setWaitlistForm({ ...waitlistForm, name: e.target.value })}
                    placeholder="Your name"
                    className="mt-1.5 bg-white border-brand-beige"
                  />
                </div>
                <div>
                  <Label htmlFor="wl-email" className="text-brand-forest text-sm">Email</Label>
                  <Input
                    id="wl-email"
                    type="email"
                    data-testid="waitlist-email-input"
                    value={waitlistForm.email}
                    onChange={(e) => setWaitlistForm({ ...waitlistForm, email: e.target.value })}
                    placeholder="you@email.com"
                    className="mt-1.5 bg-white border-brand-beige"
                  />
                </div>
                <div>
                  <Label htmlFor="wl-phone" className="text-brand-forest text-sm">Phone <span className="text-brand-inkSoft/60">(optional)</span></Label>
                  <Input
                    id="wl-phone"
                    data-testid="waitlist-phone-input"
                    value={waitlistForm.phone}
                    onChange={(e) => setWaitlistForm({ ...waitlistForm, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="mt-1.5 bg-white border-brand-beige"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="waitlist-submit-btn"
                  disabled={waitlistSubmitting}
                  className="group w-full inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-sm tracking-wide bg-brand-forest text-brand-cream hover:-translate-y-0.5 hover:shadow-xl transition-transform duration-300 disabled:opacity-60"
                >
                  {waitlistSubmitting ? (
                    <><Loader2 size={18} className="animate-spin" /> Adding you…</>
                  ) : (
                    <>Add me to the waitlist <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" /></>
                  )}
                </button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Marquee items={INCLUDED.slice(0, 5)} />

      {/* included */}
      <section className="py-28 md:py-36 bg-brand-beige/40" data-testid="included">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="arch overflow-hidden aspect-[3/4] max-w-md">
              <img src={IMAGES.candle} alt="A candle-lit reflection space" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-7 md:pl-8">
            <FadeUp><span className="overline">Always Included</span></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-forest tracking-tight mt-5 leading-[1.05]">
                Everything you need to hold your becoming.
              </h2>
            </FadeUp>
            <Stagger className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-10" stagger={0.07}>
              {INCLUDED.map((item) => (
                <StaggerItem key={item}>
                  <div className="flex items-center gap-3 py-2 border-b border-brand-beige">
                    <SunriseCircle size={18} className="text-brand-gold shrink-0" strokeWidth={1.5} />
                    <span className="text-brand-inkSoft">{item}</span>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-28 md:py-40" data-testid="faq">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <FadeUp><span className="overline">Your Questions, Answered</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif text-4xl md:text-6xl text-brand-forest tracking-tight mt-5 leading-[1.02]">
              What to expect.
            </h2>
          </FadeUp>

          <FadeUp delay={0.15}>
            <Accordion type="single" collapsible className="mt-14" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-brand-beige" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="font-serif text-2xl md:text-3xl text-brand-forest text-left hover:no-underline py-7">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-brand-inkSoft text-lg leading-relaxed pb-7">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
